import { Box, Checkbox, FormControlLabel, Stack } from "@mui/material";
import {
  pickersLayoutClasses,
  PickersLayoutContentWrapper,
  PickersLayoutProps,
  usePickerLayout,
} from "@mui/x-date-pickers-pro";
import { DateRange } from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";
import { RefAttributes, type JSX } from "react";
import {
  FontWeights,
  neutral,
  NO_TRANSLATE_CLASS,
  Text,
  VariantStyle,
} from "@flat-ui";
import { DateRangePickerProps } from "@components/date-range-picker/date-range-picker-types";
import { formatRangeHeader } from "@components/date-range-picker/date-range-picker-utils";

export type PickersRangeLayoutProps = PickersLayoutProps<
  DateRange<DateTime>,
  DateTime,
  "day"
> &
  RefAttributes<HTMLDivElement>;

type CustomRangeCalendarLayoutProps = PickersRangeLayoutProps &
  Pick<
    DateRangePickerProps,
    | "pickerTitle"
    | "shouldEnableCompare"
    | "compareLabel"
    | "isCompareChecked"
    | "onCompareChange"
  > & {
    variantStyle: VariantStyle;
  };

/**
 * Custom layout used by the DateRangePicker popper.
 * It renders the title, shortcuts, selected range header, calendar content,
 * optional compare checkbox, and action bar with FARO styling.
 *
 * @returns The styled date-range calendar layout for the picker popper.
 */
export function CustomRangeCalendarLayout({
  variantStyle,
  pickerTitle,
  shouldEnableCompare,
  compareLabel,
  isCompareChecked,
  onCompareChange,
  ...rest
}: CustomRangeCalendarLayoutProps): JSX.Element {
  const { shortcuts, content, actionBar } = usePickerLayout(rest);

  const rangeHeader = formatRangeHeader(rest.value ?? [null, null]);
  const hasSelectedRange = Boolean(rangeHeader);

  return (
    <Stack
      sx={{
        backgroundColor: variantStyle.backgroundColor,
        borderRadius: "0.25rem",
        border: `1px solid ${variantStyle.dividerColor}`,
      }}
    >
      {pickerTitle && (
        <Box sx={{ px: 3, pt: 2, pb: 0 }}>
          <Text variant="bodyM" color={variantStyle.secondaryTextColor}>
            {pickerTitle}
          </Text>
        </Box>
      )}
      {shortcuts && (
        <Box
          className={NO_TRANSLATE_CLASS}
          sx={{
            px: 3,
            pt: 1.5,
            boxSizing: "border-box",
            width: "19rem",
            maxWidth: "100%",
            "& .MuiList-root": {
              display: "flex",
              flexWrap: "wrap",
              width: "100%",
              maxWidth: "none !important",
              gap: 1.5,
              p: 0,
              // Override MUI inline maxWidth: 200 while preserving parent width.
            },
            "& .MuiListItem-root": {
              width: "auto",
              flex: "0 0 auto",
              p: 0,
            },
            "& .MuiChip-root": {
              width: "auto",
              height: "auto",
              backgroundColor: variantStyle.backgroundColor,
              color: variantStyle.secondaryTextColor,
              border: `1px solid ${neutral[200]}`,
              "&:hover": {
                backgroundColor: `${variantStyle.hoverBackgroundColor}26`,
              },
              "&.DateRangePicker-shortcutActive": {
                backgroundColor: `${variantStyle.selectedColor}14`,
                color: variantStyle.selectedColor,
                border: `1px solid ${variantStyle.selectedColor}`,
                fontWeight: FontWeights.SemiBold,
              },
              "&.DateRangePicker-shortcutActive:hover": {
                backgroundColor: `${variantStyle.selectedColor}24`,
              },
            },
            "& .MuiChip-label": {
              padding: "5px 8px",
              textTransform: "none",
              whiteSpace: "nowrap",
            },
          }}
        >
          {shortcuts}
        </Box>
      )}
      <Box
        sx={{
          px: 3,
          pt: 1.5,
          minHeight: "2.125rem",
          visibility: hasSelectedRange ? "visible" : "hidden",
        }}
      >
        <Text variant="heading20">{rangeHeader?.start}</Text>
        {" - "}
        <Text variant="heading20" color={variantStyle.secondaryTextColor}>
          {rangeHeader?.end}
        </Text>
      </Box>
      <Box
        className={NO_TRANSLATE_CLASS}
        sx={{
          "& .MuiPickersSlideTransition-root": {
            minHeight: "unset",
            height: "auto",
            display: "flex",
            flexDirection: "column",
          },
          "& .MuiDayCalendar-root": {
            height: "auto",
          },
          "& .MuiDayCalendar-monthContainer": {
            position: "relative !important",
          },
          "& .MuiPickersCalendarHeader-label": {
            color: variantStyle.secondaryColor,
            fontSize: "0.75rem",
          },
          "& .MuiPickersCalendarHeader-switchViewButton": {
            color: variantStyle.secondaryTextColor,
          },
          "& .MuiPickersArrowSwitcher-button": {
            color: variantStyle.secondaryTextColor,
          },
          "& .MuiPickersArrowSwitcher-button .MuiSvgIcon-root": {
            color: variantStyle.secondaryTextColor,
            fontSize: "1.5rem",
          },
          "& .MuiDayCalendar-weekDayLabel": {
            color: variantStyle.secondaryTextColor,
            fontWeight: FontWeights.SemiBold,
            fontSize: "0.875rem",
          },
          "& .MuiDateRangePickerDay-rangeIntervalDayHighlight": {
            backgroundColor: `${variantStyle.selectedColor}1A`,
          },
          "& .MuiDateRangePickerDay-rangeIntervalDayPreview": {
            borderColor: `${variantStyle.selectedColor}1A`,
          },
          "& .MuiDateRangePickerDay-day": {
            color: variantStyle.secondaryColor,
            fontSize: "0.875rem",
            fontWeight: FontWeights.Regular,
            "&:hover": {
              backgroundColor: `${variantStyle.hoverBackgroundColor}26`,
              color: variantStyle.selectedColor,
            },
            "&.Mui-selected": {
              backgroundColor: variantStyle.selectedColor,
              color: variantStyle.textColorSelected,
              fontWeight: FontWeights.SemiBold,
              "&:hover, &:focus": {
                backgroundColor: variantStyle.selectedColor,
              },
            },
            "&.MuiPickersDay-dayOutsideMonth": {
              color: variantStyle.disabledSecondaryTextColor,
            },
          },
        }}
      >
        <PickersLayoutContentWrapper
          className={pickersLayoutClasses.contentWrapper}
        >
          {content}
        </PickersLayoutContentWrapper>
      </Box>
      {shouldEnableCompare && (
        <FormControlLabel
          sx={{
            mx: 1.5,
            "& .MuiFormControlLabel-label": {
              fontSize: "0.875rem",
              color: variantStyle.secondaryColor,
            },
          }}
          control={
            <Checkbox
              checked={isCompareChecked}
              onChange={(event) => onCompareChange?.(event.target.checked)}
              sx={{
                color: variantStyle.secondaryColor,
                "&.Mui-checked": {
                  color: variantStyle.selectedColor,
                },
              }}
            />
          }
          label={compareLabel}
        />
      )}
      <Box sx={{ "& .MuiDialogActions-root": { px: 1.5 } }}>{actionBar}</Box>
    </Stack>
  );
}
